import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

// Color tones for the small icon badge in each tile.
// Matches the clean, light style of modern SaaS dashboards.
const ICON_TONES = {
  blue:    "bg-blue-50    text-blue-600    ring-blue-100",
  indigo:  "bg-indigo-50  text-indigo-600  ring-indigo-100",
  violet:  "bg-violet-50  text-violet-600  ring-violet-100",
  cyan:    "bg-cyan-50    text-cyan-600    ring-cyan-100",
  emerald: "bg-emerald-50 text-emerald-600 ring-emerald-100",
  amber:   "bg-amber-50   text-amber-600   ring-amber-100",
  rose:    "bg-rose-50    text-rose-600    ring-rose-100",
  slate:   "bg-slate-100  text-slate-600   ring-slate-200",
}

const DELTA_TONES = {
  positive: "text-emerald-600",
  negative: "text-rose-600",
  neutral:  "text-slate-500",
}

// One KPI card — clean white background, small badge, bold value, optional delta line
const Tile = ({ icon, iconTone = "blue", label, value, sub, delta }) => (
  <div className="bg-white border border-slate-200 rounded-xl px-4 py-3.5 shadow-sm shadow-slate-900/[0.03]">
    <div className="flex items-start justify-between">
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">
        {label}
      </div>
      <div className={`shrink-0 w-7 h-7 grid place-items-center rounded-lg ring-1 ${ICON_TONES[iconTone] || ICON_TONES.blue}`}>
        <FontAwesomeIcon icon={icon} className="w-3 h-3" />
      </div>
    </div>
    <div className="mt-2 text-[26px] leading-none font-bold tabular-nums text-slate-900">
      {value}
    </div>
    {sub && (
      <div className="mt-1.5 text-[11.5px] text-slate-500 truncate">
        {sub}
      </div>
    )}
    {delta && (
      <div className={`mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-medium ${DELTA_TONES[delta.tone] || DELTA_TONES.neutral}`}>
        <FontAwesomeIcon icon={delta.icon || "arrow-trend-up"} className="w-2.5 h-2.5" />
        {delta.label}
      </div>
    )}
  </div>
)

const KpiStrip = ({ kpis }) => {
  const fmt = (m) => {
    if (!m || m.cents == null) return "—"
    const value = (m.cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    return `${value} ${m.symbol || "€"}`
  }

  const conversionDelta = (kpis.conversion_rate ?? 0) >= 30
    ? { tone: "positive", icon: "arrow-trend-up",   label: `${kpis.conversion_rate}%` }
    : (kpis.conversion_rate ?? 0) > 0
      ? { tone: "neutral",  icon: "arrow-right",       label: `${kpis.conversion_rate}%` }
      : { tone: "negative", icon: "arrow-trend-down",  label: I18n.t("chantiers_dashboard.kpis.no_conversion") }

  const overdueDelta = (kpis.overdue_amount?.cents || 0) > 0
    ? { tone: "negative", icon: "arrow-trend-down", label: `${kpis.overdue_count} ${I18n.t("chantiers_dashboard.kpis.overdue_installments")}` }
    : null

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
      <Tile
        icon     = "hard-hat"
        iconTone = "blue"
        label    = {I18n.t("chantiers_dashboard.kpis.projects")}
        value    = {kpis.projects_total ?? 0}
        sub      = {`${kpis.projects_active ?? 0} ${I18n.t("chantiers_dashboard.kpis.active")}`}
      />
      <Tile
        icon     = "file-signature"
        iconTone = "indigo"
        label    = {I18n.t("chantiers_dashboard.kpis.signed_contract")}
        value    = {fmt(kpis.signed_contract)}
        sub      = {I18n.t("chantiers_dashboard.kpis.signed_contract_hint")}
      />
      <Tile
        icon     = "file-invoice-dollar"
        iconTone = "cyan"
        label    = {I18n.t("chantiers_dashboard.kpis.invoiced")}
        value    = {fmt(kpis.invoiced)}
      />
      <Tile
        icon     = "money-check-dollar"
        iconTone = "emerald"
        label    = {I18n.t("chantiers_dashboard.kpis.paid")}
        value    = {fmt(kpis.paid)}
      />
      <Tile
        icon     = "hourglass-half"
        iconTone = "amber"
        label    = {I18n.t("chantiers_dashboard.kpis.outstanding")}
        value    = {fmt(kpis.outstanding)}
      />
      <Tile
        icon     = "triangle-exclamation"
        iconTone = "rose"
        label    = {I18n.t("chantiers_dashboard.kpis.overdue")}
        value    = {fmt(kpis.overdue_amount)}
        delta    = {overdueDelta}
      />

      <Tile
        icon     = "file-lines"
        iconTone = "violet"
        label    = {I18n.t("chantiers_dashboard.kpis.quotes")}
        value    = {kpis.quotes_total ?? 0}
        sub      = {`${kpis.quotes_accepted ?? 0} ${I18n.t("chantiers_dashboard.kpis.quotes_accepted")}`}
        delta    = {conversionDelta}
      />
      <Tile
        icon     = "circle-question"
        iconTone = "slate"
        label    = {I18n.t("chantiers_dashboard.kpis.quotes_pending")}
        value    = {fmt(kpis.quotes_pending)}
        sub      = {I18n.t("chantiers_dashboard.kpis.quotes_pending_hint")}
      />
    </div>
  )
}

export default KpiStrip
